import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { TabsContent } from '../ui/tabs';
import {
  Plus,
  Eye,
  Trash2,
  User,
  RefreshCw,
  GitMerge,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ListPager, usePagedList } from './ListPager';
import { ListToolbar, useFilteredList } from './ListToolbar';
import { LANG_LABELS_JA, LANG_FILTER_OPTIONS, LangCounts } from './GuidesTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsersTab = ({
  users,
  showAddUserModal,
  setShowAddUserModal,
  newUserData,
  setNewUserData,
  addingUser,
  handleAddUser,
  formatDateShort,
  getImageUrl,
  getAuthHeaders,
  fetchData,
  setSelectedItem,
  setDialogType,
  setDialogOpen,
  onOpenDuplicates,
}) => {
  const { t, language } = useLanguage();
  const filter = useFilteredList(
    users,
    ['name', 'nameKana', 'email', 'phone', 'company'],
    [{ key: 'role', label: t('admin.users.userRole'), options: [
      { value: 'user', label: 'ユーザー' },
      { value: 'admin', label: '管理者' },
    ] },
    { key: 'language', label: '言語', get: u => u.language || 'none', options: LANG_FILTER_OPTIONS }]
  );
  const pager = usePagedList(filter.result);
  return (
    <>
      <TabsContent value="users" className="p-0">
        {/* Header with Add Button */}
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-semibold">{t('admin.users.title')}</h3>
          <div className="flex gap-2">
            <Button onClick={() => onOpenDuplicates && onOpenDuplicates()} size="sm" variant="outline" data-testid="open-duplicate-users-btn">
              <GitMerge className="h-4 w-4 mr-1" /> 重複アカウント整理
            </Button>
            <Button onClick={() => setShowAddUserModal(true)} size="sm" className="bg-[#00A651] hover:bg-[#008c44]">
              <Plus className="h-4 w-4 mr-1" /> {t('admin.common.addNew')}
            </Button>
          </div>
        </div>
        <div className="px-4 pt-3">
          <ListToolbar filter={filter} testId="users" placeholder="氏名・カナ・メール・電話・会社名で検索" />
          <LangCounts codes={filter.result.map(u => u.language)} label="言語別人数" testId="users" />
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">{t('admin.users.registrationDate')}</TableHead>
                <TableHead>{t('admin.common.name')}</TableHead>
                <TableHead>言語</TableHead>
                <TableHead>{t('admin.common.email')}</TableHead>
                <TableHead>{t('admin.form.phone')}</TableHead>
                <TableHead>{t('admin.form.company')}</TableHead>
                <TableHead className="w-[80px]">{t('admin.users.userRole')}</TableHead>
                <TableHead className="w-[100px]">{t('admin.users.idDocument')}</TableHead>
                <TableHead className="w-[100px]">{t('admin.users.qualifications')}</TableHead>
                <TableHead className="w-[100px]">{t('admin.common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pager.total === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                    {t('admin.users.noUsers')}
                  </TableCell>
                </TableRow>
              ) : (
                pager.paged.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="text-xs text-gray-500">
                      {formatDateShort(user.createdAt)}
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {user.profileImage ? (
                          <img 
                            src={getImageUrl(user.profileImage)} 
                            alt="" 
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <div>{user.name}</div>
                          {user.nameKana && <div className="text-xs text-gray-400">{user.nameKana}</div>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell data-testid={`user-language-${user.id}`}>
                      {user.language ? <span className="text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap">{LANG_LABELS_JA[user.language] || user.language}</span> : '-'}
                    </TableCell>
                    <TableCell className="text-sm">{user.email}</TableCell>
                    <TableCell className="text-sm">{user.phone || '-'}</TableCell>
                    <TableCell className="text-sm">{user.company || '-'}</TableCell>
                    <TableCell>
                      <Badge className={user.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}>
                        {user.role === 'admin' ? '管理者' : 'ユーザー'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {user.idDocumentFront ? (
                          <a 
                            href={getImageUrl(user.idDocumentFront)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            表
                          </a>
                        ) : <span className="text-xs text-gray-300">-</span>}
                        {user.idDocumentBack ? (
                          <a 
                            href={getImageUrl(user.idDocumentBack)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            裏
                          </a>
                        ) : <span className="text-xs text-gray-300">-</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {user.certDocumentFront ? (
                          <a 
                            href={getImageUrl(user.certDocumentFront)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            表
                          </a>
                        ) : <span className="text-xs text-gray-300">-</span>}
                        {user.certDocumentBack ? (
                          <a 
                            href={getImageUrl(user.certDocumentBack)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            裏
                          </a>
                        ) : <span className="text-xs text-gray-300">-</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedItem(user);
                            setDialogType('viewUser');
                            setDialogOpen(true);
                          }}
                          className="h-8 w-8"
                          data-testid={`view-user-${user.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={async () => {
                            if (confirm('このユーザーを削除しますか？')) {
                              try {
                                await axios.delete(`${API}/admin/users/${user.id}`, { headers: getAuthHeaders() });
                                toast.success('ユーザーを削除しました');
                                fetchData();
                              } catch (error) {
                                toast.error('削除に失敗しました');
                              }
                            }
                          }}
                          className="h-8 w-8 text-red-500 hover:text-red-700"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="px-4">
          <ListPager pager={pager} testId="users" />
        </div>
      </TabsContent>

      {/* Add User Modal */}
      <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin.common.addNew')}</DialogTitle>
            <DialogDescription>{t('admin.common.addNew')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div>
              <Label>{t('admin.common.name')} <span className="text-red-500">*</span></Label>
              <Input
                value={newUserData.name}
                onChange={(e) => setNewUserData({...newUserData, name: e.target.value})}
                placeholder="山田 太郎"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>フリガナ</Label>
              <Input
                value={newUserData.nameKana}
                onChange={(e) => setNewUserData({...newUserData, nameKana: e.target.value})}
                placeholder="ヤマダ タロウ"
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('admin.common.email')} <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                placeholder="example@email.com"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>パスワード <span className="text-red-500">*</span></Label>
              <Input
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                placeholder="6文字以上"
                required
                minLength={6}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('admin.form.phone')}</Label>
              <Input
                value={newUserData.phone}
                onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})}
                placeholder="090-1234-5678"
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('admin.form.company')}</Label>
              <Input
                value={newUserData.company}
                onChange={(e) => setNewUserData({...newUserData, company: e.target.value})}
                placeholder="株式会社〇〇"
                className="mt-1"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddUserModal(false)}>
                キャンセル
              </Button>
              <Button type="submit" disabled={addingUser} className="bg-[#00A651] hover:bg-[#008c44]">
                {addingUser ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> {t('admin.common.saving')}</> : t('admin.common.add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UsersTab;
